import { useState } from 'react';
import { Palette, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Avatar } from '../components/Avatar';
import { DarkModeToggle } from '../components/DarkModeToggle';
import { useKineProfile, type KineProfile } from '../../lib/supabase/queries/useKineProfile';
import { useAuth } from '../components/auth/AuthProvider';
import { KineHeader } from '../components/kine/KineHeader';
import { KineSidebar } from '../components/kine/KineSidebar';

function PerfilProfesionalCard({
  kineProfile,
  error,
  loading,
}: {
  kineProfile: KineProfile | null;
  error: string | null;
  loading: boolean;
}) {
  const cardClass =
    "bg-card rounded-2xl p-6 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)] mb-6";
  const headerClass =
    "text-xs uppercase tracking-[0.08em] text-muted-foreground mb-4";

  if (loading) {
    return (
      <div className={cardClass}>
        <h2 className={headerClass}>Perfil profesional</h2>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-muted animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-5 w-40 bg-muted rounded animate-pulse" />
            <div className="h-4 w-56 bg-muted rounded animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.08em] text-muted-foreground mb-1">Email</p>
            <div className="h-4 w-48 bg-muted rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !kineProfile) {
    return (
      <div className={cardClass}>
        <h2 className={headerClass}>Perfil profesional</h2>
        <p className="text-sm text-orange-500">
          No pudimos cargar tu perfil. Intentá recargar la página.
        </p>
      </div>
    );
  }

  // Sin prefijo hardcoded — la matrícula incluye su propio prefijo
  // (MN/MP) según provincia, lo completa el kine en signup.
  const subtitulo = kineProfile.matricula
    ? `Kinesiólogo · ${kineProfile.matricula}`
    : "Kinesiólogo";

  return (
    <div className={cardClass}>
      <h2 className={headerClass}>Perfil profesional</h2>
      <div className="flex items-center gap-4 mb-6">
        <Avatar name={kineProfile.nombre} size="xl" colorIndex={0} />
        <div className="flex-1">
          <h3 className="text-xl font-semibold">{kineProfile.nombre}</h3>
          <p className="text-sm text-muted-foreground">{subtitulo}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.08em] text-muted-foreground mb-1">Email</p>
          <p className="text-sm">{kineProfile.email}</p>
        </div>
      </div>
    </div>
  );
}

export function ConfiguracionKine() {
  const { data: kineProfile, error, loading } = useKineProfile();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background transition-colors duration-200">
      <KineHeader
        kineNombre={kineProfile?.nombre || "—"}
        onMenuClick={() => setDrawerOpen(true)}
      />

      <div className="flex">
        <KineSidebar
          activeKey="configuracion"
          mobileOpen={drawerOpen}
          onMobileClose={() => setDrawerOpen(false)}
        />

        <div className="flex-1 p-6">
          <div className="max-w-[800px]">
            <h1 className="text-3xl mb-6">Configuración</h1>

            <PerfilProfesionalCard
              kineProfile={kineProfile}
              error={error}
              loading={loading}
            />

            <div className="space-y-3">
              <h2 className="text-xs uppercase tracking-[0.08em] text-muted-foreground mb-3 px-2">Preferencias</h2>

              <div className="bg-card rounded-xl px-4 py-3 border border-border shadow-sm flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                    <Palette className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <span className="font-medium">Modo oscuro</span>
                </div>
                <DarkModeToggle />
              </div>

            </div>

            <button
              onClick={handleLogout}
              className="w-full bg-card rounded-xl px-4 py-3 border border-border shadow-sm flex items-center justify-between mt-6 transition-all duration-200 ease-out hover:bg-destructive/10"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-destructive/10 rounded-xl flex items-center justify-center">
                  <LogOut className="w-5 h-5 text-destructive" strokeWidth={1.5} />
                </div>
                <span className="font-medium text-destructive">Cerrar sesión</span>
              </div>
            </button>

            <p className="text-xs text-center text-muted-foreground mt-6">
              Gonix v1.0.0 · Plataforma profesional
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
